// Settings › CRM 2.0 › Customer Consent › Documents (M1-F6) — as prototype A21t (kDocs).
// The four flow documents and their rules. Read only: the rules are part of the
// flow; Legal makes each template in Template Builder (M12). Templates come from
// DocTemplates / DocTemplateLinks; extra terms from the Terms tab.
import React, { useEffect, useState } from "react";
import { supabase } from "../../../supabaseClient";
import { getConsentTerms, consentError } from "../../../Services/Crm2Consent";
import cssClasses from "../Settings/Crm2Settings.module.css";

const DOCS = [
  {
    name: "Application for Booking",
    made: "CRM tagged (Day 0) · sent with the Application for Booking email",
    who: "Portal contact person (applicants are added later, in the Agreement KYC)",
    how: "Read the draft on the portal · confirm by OTP (like the review draft)",
    waits: "Agreement KYC stays locked until it is confirmed · Final Review waits",
    versions: "Price, plan or parking change → new version shown as the latest; no new confirmation; old version kept as Replaced",
    kind: "AFB",
    point: "afb_confirm",
  },
  {
    name: "Review draft",
    made: "After the Agreement KYC is submitted",
    who: "Every applicant",
    how: "Confirm by OTP",
    waits: "Final Review",
    versions: "Every revision kept; a KYC change sends a new revision for OTP",
    kind: "AGREEMENT",
    point: "review_confirm",
  },
  {
    name: "Allotment letter",
    made: "Final Review approved · stored and locked (one version)",
    who: "Any one applicant",
    how: "Aadhaar eSign · Aadhaar name must match an applicant",
    waits: "Allotted · FarVision Sync",
    versions: "One version; no reissue (Legal-approved template). VJ side treated as signed (DSC: later phase)",
    kind: "ALLOTMENT_LETTER",
    point: "letter_esign",
  },
  {
    name: "Final draft",
    made: "CRM clicks Generate Final Draft",
    who: "Every applicant",
    how: "Confirm by OTP",
    waits: "Process for Stamp",
    versions: "Every version kept; a new version cancels the OTP; all applicants confirm again",
    kind: "AGREEMENT",
    point: "final_confirm",
  },
];
const ROWS = [["Made", "made"], ["Who confirms", "who"], ["How", "how"], ["What waits", "waits"], ["Versions", "versions"]];
const TPL_STATUS = { DRAFT: "Draft", IN_APPROVAL: "In approval", RETURNED: "Returned", APPROVED: "Approved", LIVE: "Live", RETIRED: "Retired" };

const chip = (text, color, background) => (
  <span style={{ fontSize: "1.2rem", fontFamily: "Inter-Medium", color, background, borderRadius: "1.6rem", padding: "0.2rem 1rem", whiteSpace: "nowrap" }}>{text}</span>
);
const statusChip = (s) =>
  s === "APPROVED" || s === "LIVE" ? chip(TPL_STATUS[s], "#027A48", "#ECFDF3") : s === "DRAFT" ? chip(TPL_STATUS[s], "#175CD3", "#EFF8FF") : chip(TPL_STATUS[s] || s, "#B54708", "#FFFAEB");

function Crm2ConsentDocuments() {
  const [templates, setTemplates] = useState([]); // { kind, project, name, version, status }
  const [termCount, setTermCount] = useState({});

  useEffect(() => {
    (async () => {
      const [tpl, links] = await Promise.all([
        supabase.from("DocTemplates").select("docTemplateId, templateName, templateTypeKey, versionNo, status").in("templateTypeKey", ["AFB", "AGREEMENT", "ALLOTMENT_LETTER"]),
        supabase.from("DocTemplateLinks").select("docTemplateId, projectId(projectName)"),
      ]);
      if (tpl.error || links.error) {
        console.error("[crm2][Crm2ConsentDocuments] templates load failed", { templates: tpl.error?.message, links: links.error?.message });
        return;
      }
      const projectsOf = {};
      (links.data || []).forEach((l) => {
        (projectsOf[l.docTemplateId] = projectsOf[l.docTemplateId] || []).push(l.projectId?.projectName || "Company default");
      });
      setTemplates(
        (tpl.data || []).map((t) => ({
          kind: t.templateTypeKey,
          project: (projectsOf[t.docTemplateId] || ["Not linked"]).join(", "),
          name: t.templateName || "—",
          version: t.versionNo,
          status: t.status,
        }))
      );
    })();
    getConsentTerms()
      .then((terms) => {
        const c = {};
        terms.forEach((t) => (c[t.pointKey] = (c[t.pointKey] || 0) + 1));
        setTermCount(c);
      })
      .catch((err) => console.error("[crm2][Crm2ConsentDocuments] terms load failed", { message: consentError(err).message }));
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.6rem" }}>
      <p className={cssClasses.headSub}>
        Four documents of the CRM 2.0 flow. Their rules are part of the flow (not editable here); Legal makes each template in Template Builder.
      </p>
      {DOCS.map((d) => {
        const rows = templates.filter((t) => t.kind === d.kind);
        return (
          <div key={d.name} className={cssClasses.card}>
            <p className={cssClasses.cardTitle} style={{ display: "flex", gap: "0.8rem", alignItems: "center" }}>
              {d.name} {chip("Document", "#3538CD", "#EEF4FF")}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "11rem minmax(0, 1fr)", gap: "0.6rem 1.2rem", fontSize: "1.3rem" }}>
              {ROWS.map(([label, key]) => (
                <React.Fragment key={key}>
                  <span style={{ color: "#667085" }}>{label}</span>
                  <span style={{ color: "#101828" }}>{d[key]}</span>
                </React.Fragment>
              ))}
              <span style={{ color: "#667085" }}>Extra terms</span>
              <span style={{ color: "#101828" }}>
                {termCount[d.point] ? `${termCount[d.point]} on consent point ${d.point}` : `None · Legal can add terms on ${d.point}`}
              </span>
            </div>
            <div className={cssClasses.tableCard}>
              <table className={cssClasses.table}>
                <thead><tr><th>Project</th><th>Template</th><th>Status</th></tr></thead>
                <tbody>
                  {rows.length ? rows.map((t, i) => (
                    <tr key={i}><td>{t.project}</td><td>{t.name} · v{t.version}</td><td>{statusChip(t.status)}</td></tr>
                  )) : (
                    <tr><td colSpan={3} className={cssClasses.hint}>No template yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      <p className={cssClasses.hint}>
        What is saved for every confirmation: the PDF of the version shown, the template version, who confirmed, when, the mobile number and that the OTP was checked on
        the server (never the OTP value). eSign: the signed PDF and the eSign record (signer name, last 4 Aadhaar digits, time, provider transaction id).
      </p>
    </div>
  );
}

export default Crm2ConsentDocuments;
